import { useState, useEffect } from 'react';
import { readContract } from '@wagmi/core';
import postsContractABI from '../../contracts/tgetherPosts.json';
import * as config from '../../config.tsx';

const postsContractAddress = process.env.REACT_APP_POSTS_CONTRACT_ADDRESS as `0x${string}`;

interface PostType {
  tokenId: string;
  title: string;
  authorAddress: string;
  authorName: string;
  content: string;
  description?: string; // Optional property
}

function useFetchPosts(account: string | undefined) {
  const [posts, setPosts] = useState<PostType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPosts = async () => {
      if (!account) {
        setError('No account provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch the number of tokens owned by the account
        const balanceOf = await readContract(config.config, {
          address: postsContractAddress,
          abi: postsContractABI.abi,
          functionName: 'balanceOf',
          args: [account],
        });

        const ownedPosts: PostType[] = [];

        // Loop through the balance count and fetch posts manually
        for (let i = 0; i < Number(balanceOf); i++) {
          // Fetch the token ID by index
          const tokenId = await fetchTokenId(account, i);

          if (tokenId) {
            // Fetch the post details for the token ID
            const postData = await fetchPostDetails(tokenId);

            if (postData) {
              ownedPosts.push(postData);
            }
          }
        }

        setPosts(ownedPosts);
      } catch (error) {
        console.error('Error fetching posts:', error);
        setError('Error fetching posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [account]);

  // Separate function to fetch token ID using wagmi
  const fetchTokenId = async (account: string, index: number): Promise<string | null> => {
    try {
      const tokenId = await readContract(config.config, {
        address: postsContractAddress,
        abi: postsContractABI.abi,
        functionName: 'tokenOfOwnerByIndex',
        args: [account, index],
      });

      return tokenId as string;
    } catch (error) {
      console.error('Error fetching token ID:', error);
      return null;
    }
  };

  // Separate function to fetch post details using wagmi
  const fetchPostDetails = async (tokenId: string): Promise<PostType | null> => {
    try {
      // Convert tokenId to a string using `.toString()` in case it's a BigInt
      const tokenIdString = BigInt(tokenId).toString();
  
      const postData = await readContract(config.config, {
        address: postsContractAddress,
        abi: postsContractABI.abi,
        functionName: 'posts',
        args: [tokenIdString],
      });
  
      if (postData) {
  
        return {
          tokenId: tokenIdString,  // Use the converted string version of tokenId
          title: postData[0] as string,       // Assuming title is at index 0
          content: postData[1] as string,     // Assuming content is at index 1
          authorAddress: postData[2] as string,  // Assuming authorAddress is at index 2
          authorName: postData[3] as string,  // Assuming authorName is at index 3
          description: postData[4] as string, // Assuming description is at index 4
        };
      }
  
      return null; // If no data is found
    } catch (error) {
      console.error('Error fetching post details:', error);
      return null;
    }
  };
  
  

  return { posts, loading, error };
}

export default useFetchPosts;
